import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  MenuItem,
  IconButton
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AdvertisingLayout from '../../layouts/AdvertisingLayout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SchoolIcon from '@mui/icons-material/School';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import LinkIcon from '@mui/icons-material/Link';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { API_URL } from '../../config';

const Advertising = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    phoneNumber: '',
    emailAddress: '',
    campaignArea: '',
    message: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Prepare data for API
      const enquiryData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        businessName: formData.businessName,
        phone: formData.phoneNumber,
        email: formData.emailAddress,
        campaignType: formData.campaignArea, // Maps to campaignType in database
        customerNotes: formData.message,
        leadMethod: 'Website' // Default to Website as specified
      };
      
      // Call the public enquiries endpoint
      const response = await fetch(`${API_URL}/enquiries/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enquiryData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          businessName: '',
          phoneNumber: '',
          emailAddress: '',
          campaignArea: '',
          message: ''
        });
        
        // Show success message
        alert('Thank you for your enquiry! Our advertising team will contact you soon.');
      } else {
        throw new Error(data.message || 'Failed to submit enquiry');
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('There was an error submitting your enquiry. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Target Your Audience',
      description: 'Reach parents, families, business owners, and community members directly through their local school.'
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Affordable Pricing',
      description: 'School Newsletter Advertising per school term costs as little as $180.00 inc GST per campaign.'
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Support Local Schools',
      description: 'Choosing to advertise in local school newsletters (digital or printed) allows the school to continue their current format for free.'
    },
    {
      icon: <DesignServicesIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Free Design Services',
      description: 'Each advertising campaign includes free graphic design services for a professionally finished image.'
    },
    {
      icon: <LinkIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Digital Integration',
      description: 'Link your digital ad to your website, landing page, social media platform or email address.'
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 40, color: '#247AA8' }} />,
      title: 'Professional Consultation',
      description: 'Work with a professional Advertising Consultant to customise your campaign.'
    }
  ];

  const testimonials = [
    {
      company: 'Hendra Hardware',
      author: 'Matthew Pinkey',
      quote: 'Owning an independent Hardware Store for over 10 years has certainly met some serious challenges, however partnering with Austnews has certainly given me some great reinforcements to my cavalry. Dealing with the team from Austnews has always been a pleasure, thankyou for the efficient and effective services that they have been provided to me for the last 8 years.'
    },
    {
      company: 'Flip out Strathpine',
      author: 'Patricia Gibbons',
      quote: 'Austnews has made it easy for us to advertise to our target market. The entire process is always extremely well managed with efficiency and professionalism. The sales team are able to work with us on advertising strategies that meet our marketing objectives.'
    },
    {
      company: 'Rhee Tae Kwon-Do Ferny Hills',
      author: 'Nick Anderson',
      quote: 'Advertising through the school newsletter has been a great way to promote our club to the school community. Most new members have seen our advertising in the newsletter, and it has encouraged them to come along and join up.'
    },
    {
      company: 'Advanced Air Conditioning',
      author: 'Danielle Page',
      quote: 'Austnews Advertising has provided our company with the ability to efficiently and effectively deliver our message and branding to our local community. We\'ve now been successfully advertising while supporting our local school for the past 4 years.'
    },
    {
      company: 'Accurate Tree Services',
      author: 'Bylinda Trivett',
      quote: 'We have been advertising with Austnews for over 18 years now & find this advertising exceptionally effective. We could not recommend Austnews highly enough to anyone looking for more clients & to grow their business.'
    },
    {
      company: 'Ray White Rural Esk',
      author: 'Kay Davis',
      quote: 'Our business is built on loyalty and trust and what a better way to reach a broad family market than the school newsletter. Austnews Advertising provides a professional look, cost effective and easy process to advertise.'
    },
    {
      company: 'Pets Domain',
      author: 'Estelle Tamplin',
      quote: 'Pets Domain has advertised in school newsletters in the past two years, this has kept our brand front and centre in the communities where our stores are located.'
    },
    {
      company: 'Tooth Dental',
      author: 'Phoebe Sampson',
      quote: 'Through advertising with Austnews across multiple Schools, Tooth has become well known for sponsoring local school and events, Sports clubs and not for profit organisations.'
    },
    {
      company: 'Bayside Dance',
      author: 'Michelle Macwhirter',
      quote: 'We have been advertising with Austnews School Newsletters for over five years, which has allowed us to communicate to school families in our local area on a regular basis.'
    },
    {
      company: 'Blue Line Dry Cleaners',
      author: 'Wayne Wilson',
      quote: 'Austnews has enabled Blue Line Dry Cleaners to bring our brand and advertising into the digital world with electronic advertising. This has played an important role in maintaining our ads in our community.'
    },
    {
      company: 'Born & Bread Bakery',
      author: 'Alison Reeves',
      quote: 'For the last 10 years we have used Austnews School Newsletters to help us advertise our business. We have found this to be a targeted and very cost effective means of advertising our bakeries.'
    },
    {
      company: 'Currumbin Gymnastics & Cheer',
      author: 'Angie Baxter',
      quote: 'Currumbin Gymnastics & Cheer have been advertising with Australian Newsletters for over a decade. It is a professional company, every section of the company is a pleasure to work with.'
    }
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotateKey, setAutoRotateKey] = useState(0);

  // Auto-rotate testimonials every 10 seconds (move by 1)
  useEffect(() => {
    if (isHovering) return; // Don't auto-rotate when hovering

    const timer = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 10000);

    return () => clearInterval(timer);
  }, [testimonials.length, isHovering, autoRotateKey]);

  const resetAutoRotate = () => {
    setAutoRotateKey((prev) => prev + 1);
  };

  const handlePrevTestimonial = () => {
    setCurrentTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    resetAutoRotate();
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    resetAutoRotate();
  };

  const handleDotClick = (index) => {
    setCurrentTestimonialIndex(index);
    resetAutoRotate();
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    resetAutoRotate();
  };

  // Get the current two testimonials to display
  const getCurrentTestimonials = () => {
    const first = testimonials[currentTestimonialIndex];
    const second = testimonials[(currentTestimonialIndex + 1) % testimonials.length];
    return [first, second];
  };

  return (
    <AdvertisingLayout>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #247AA8 0%, #1E6A92 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                School Newsletter Advertising
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontWeight: 300
                }}
              >
                Connecting School Communities
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
            Does your Business target School Communities?
          </Typography>
          
          <Typography variant="h6" paragraph align="center" sx={{ mb: 6, color: 'text.secondary' }}>
            School Newsletter Advertising will reach your target audience and give you repeated exposure at an affordable price.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3 }}>
            We are a team of professional Advertising Consultants, Graphic Designers, School Teachers and Digital Technicians creating digital communication tools for Australian Schools. Choosing to advertise on a selected school newsletter allows schools to utilise those tools for free. Therefore, creating a direct connection between the school, their community and the home.
          </Typography>
        </Container>
      </Box>

      {/* Benefits Cards */}
      <Box sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 6, fontWeight: 600 }}>
            Why School Newsletter Advertising?
          </Typography>
          
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 2 }}>
                      {benefit.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box id="testimonials" sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 6, fontWeight: 600 }}>
            Client Testimonials
          </Typography>
          
          <Grid container spacing={4}>
            {getCurrentTestimonials().map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={3}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderLeft: '4px solid #247AA8',
                    minHeight: { xs: '200px', md: '250px' },
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#247AA8' }}>
                    {testimonial.company}
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#666', mb: 2 }}>
                    {testimonial.author}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.7, flexGrow: 1 }}>
                    "{testimonial.quote}"
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Navigation Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 4,
              gap: 2
            }}
          >
            <IconButton
              onClick={handlePrevTestimonial}
              sx={{
                backgroundColor: '#247AA8',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1E6A92'
                }
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            {/* Dots Indicator */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {testimonials.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleDotClick(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: currentTestimonialIndex === index ? '#247AA8' : '#ccc',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      backgroundColor: currentTestimonialIndex === index ? '#247AA8' : '#999'
                    }
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={handleNextTestimonial}
              sx={{
                backgroundColor: '#247AA8',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1E6A92'
                }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box id="contact" sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4, fontWeight: 600 }}>
            Get Started Today
          </Typography>
          
          <Paper elevation={3} sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Business Name"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email Address"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Campaign Area/Schools"
                    name="campaignArea"
                    value={formData.campaignArea}
                    onChange={handleInputChange}
                    placeholder="e.g., Brisbane North, Specific school names, or areas you'd like to target"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your advertising needs..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      backgroundColor: '#247AA8',
                      '&:hover': {
                        backgroundColor: '#1E6A92'
                      },
                      py: 1.5
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Enquiry'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#247AA8', color: 'white', textAlign: 'center' }}>
        <Container>
          <Typography variant="body2">
            Copyright Australian Newsletter Services Pty Ltd 2016-2024 |{' '}
            <Box
              component="a"
              href="/advertising/terms-and-conditions"
              sx={{
                color: 'white',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              Advertising Terms & Conditions
            </Box>
            {' '}| All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
        </Container>
      </Box>
    </AdvertisingLayout>
  );
};

export default Advertising;

